export type ErrorCategory = "network" | "auth" | "permission" | "server" | "unknown";

type ErrorDetails = {
  category: ErrorCategory;
  status?: number;
  message: string;
  userMessage: string;
  suggestedAction?: string;
  isTransient: boolean;
};

const NETWORK_PATTERNS = [
  /failed to fetch/i,
  /network error/i,
  /networkerror/i,
  /timeout/i,
  /timed out/i,
  /offline/i,
  /load failed/i,
];

const AUTH_PATTERNS = [/jwt/i, /token/i, /unauthorized/i, /not authenticated/i, /session expired/i];

const PERMISSION_STATUSES = new Set([401, 403]);

function extractErrorMessage(error: unknown): string {
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  if (typeof error === "object" && error !== null) {
    const errorObj = error as { message?: string; error?: string; description?: string };
    return errorObj.message ?? errorObj.error ?? errorObj.description ?? String(error);
  }
  return String(error);
}

function extractStatusCode(error: unknown): number | undefined {
  if (typeof error !== "object" || error === null) return undefined;
  const errorObj = error as {
    status?: number;
    statusCode?: number;
    code?: number | string;
    context?: { status?: number };
  };
  if (typeof errorObj.status === "number") return errorObj.status;
  if (typeof errorObj.statusCode === "number") return errorObj.statusCode;
  if (typeof errorObj.context?.status === "number") return errorObj.context.status;
  if (typeof errorObj.code === "number") return errorObj.code;
  if (typeof errorObj.code === "string" && /^[0-9]+$/.test(errorObj.code)) {
    return Number(errorObj.code);
  }
  return undefined;
}

function isNetworkError(message: string): boolean {
  return NETWORK_PATTERNS.some((pattern) => pattern.test(message));
}

function isAuthErrorMessage(message: string): boolean {
  return AUTH_PATTERNS.some((pattern) => pattern.test(message));
}

export function buildErrorDetails(error: unknown): ErrorDetails {
  const message = extractErrorMessage(error);
  const status = extractStatusCode(error);

  if (status && PERMISSION_STATUSES.has(status)) {
    return {
      category: "permission",
      status,
      message,
      userMessage: "You don't have permission to view this. Contact your administrator.",
      isTransient: false,
    };
  }

  if (status && status >= 500) {
    return {
      category: "server",
      status,
      message,
      userMessage: "Something went wrong on our end. Please try again in a few minutes.",
      isTransient: true,
    };
  }

  if (isNetworkError(message)) {
    return {
      category: "network",
      status,
      message,
      userMessage: "Unable to connect. Check your internet connection and try again.",
      isTransient: true,
    };
  }

  if (isAuthErrorMessage(message)) {
    return {
      category: "auth",
      status,
      message,
      userMessage: "Your session has expired. Please sign in again.",
      isTransient: false,
    };
  }

  return {
    category: "unknown",
    status,
    message,
    userMessage: "Something unexpected happened.",
    suggestedAction: "Retry or contact support for help.",
    isTransient: true,
  };
}
